import { useConfig } from "@/core/config/ConfigContext";
import { useQueryClient, UseMutationResult } from "@tanstack/react-query";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { useServices } from "@/core/services/useServices";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { Module } from "@/constants";
import { LegacyStoreGroupService } from "../service";

export function useUpdateStoreGroupStores(
  groupId: string,
): UseMutationResult<ReadStoreGroup, Error, WriteStoreGroup, unknown> {
  if (!groupId) {
    throw new Error("groupId required for updateStores");
  }

  const config = useConfig();
  const useNewService = config.isFeatureEnabled("newStoreGroupServiceEnabled");
  const { storeGroups } = useServices();
  const queryClient = useQueryClient();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.StoreGroups);

  return useMutationWithErrorHandling<
    ReadStoreGroup,
    Error,
    WriteStoreGroup,
    unknown
  >({
    mutationFn: (data: WriteStoreGroup) => {
      if (useNewService) {
        return storeGroups.updateGroupStores(groupId, data);
      }

      return LegacyStoreGroupService.patch<WriteStoreGroup, ReadStoreGroup>(
        groupId,
        data,
        "stores",
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["storeGroupMembers", groupId],
      });

      await invalidateListQuery();
    },
  });
}
